// Reusable status card for loading/error/blocked guard states.
// Also exports renderSurveyStatus to pick the right variant.

import React from "react";
import { Button, Card, CardBody, CardHeader, Typography } from "@material-tailwind/react";
import ContentWrapper from "components/ui/content-wrapper";
import MaterialIcon from "components/material-icon";

export default function SurveyStatusCard({ icon, title, message, action }) {
    return (
        <ContentWrapper>
            <div className="lg:p-6 p-4">
                <Card shadow={false} className="card mx-auto p-4">
                    {title && (
                        <CardHeader
                            className="card-inner-header mt-4"
                            color="transparent"
                            shadow={false}
                        >
                            <Typography variant="h1" className="card-header-text">
                                {title}
                            </Typography>
                        </CardHeader>
                    )}
                    <CardBody className="card-inner-body">
                        {icon ? (
                            <Card className="bg-red-50 border-l-4 border-red-500 p-6">
                                <div className="flex flex-col gap-4">
                                    <div className="flex items-start gap-4">
                                        <MaterialIcon
                                            name={icon}
                                            className="text-red-500"
                                            size="xl"
                                        />
                                        <div className="flex-1">
                                            <Typography variant="h4" className="text-red-800 mb-2">
                                                {title}
                                            </Typography>
                                            <Typography variant="paragraph" className="text-red-700">
                                                {message}
                                            </Typography>
                                        </div>
                                    </div>
                                    {action}
                                </div>
                            </Card>
                        ) : (
                            <Typography variant="paragraph" className="text-center py-8">
                                {message}
                            </Typography>
                        )}
                    </CardBody>
                </Card>
            </div>
        </ContentWrapper>
    );
}

// Renders the appropriate SurveyStatusCard for the orchestrator's
// early-return guard clauses. Returns null when the survey is ready
// to render, so the caller can do `if (status) return status;`.
export function renderSurveyStatus({
    surveyIsLoading,
    surveyError,
    isBlocked,
    survey,
    navigate,
}) {
    if (surveyIsLoading) {
        return <SurveyStatusCard message="Loading survey..." />;
    }
    if (surveyError) {
        return (
            <SurveyStatusCard
                icon="error"
                title="Error Loading Survey"
                message="There was an error loading this survey. Please try again or contact support if the problem persists."
            />
        );
    }
    if (survey && isBlocked) {
        return (
            <SurveyStatusCard
                icon="block"
                title="Survey Access Blocked"
                message="Your response credit balance is zero and you have responses being held awaiting credit. Please purchase credits to continue managing this survey."
                action={
                    <div className="flex justify-end">
                        <Button
                            size="lg"
                            className="bg-red-500 hover:bg-red-600 text-white"
                            onClick={() => navigate("/top-ups")}
                        >
                            Buy top-up now
                        </Button>
                    </div>
                }
            />
        );
    }
    if (!survey) {
        return <SurveyStatusCard message="Loading survey..." />;
    }
    return null;
}
